import { useState, useEffect } from 'react';
import { UserPlus, UserCheck, UserMinus, Loader2, Send } from 'lucide-react';
import connectionService from '../../services/api/connection.service';
import { toast } from 'react-hot-toast';
import Button from '@components/common/Button';

const ConnectionActions = ({ userId, onStatusChange }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [connectionMessage, setConnectionMessage] = useState('');

  useEffect(() => {
    fetchConnectionStatus();
  }, [userId]);

  const fetchConnectionStatus = async () => {
    try {
      setLoading(true);
      const response = await connectionService.getConnectionStatus(userId);
      if (response.success) {
        setStatus(response.data);
        if (onStatusChange) {
          onStatusChange(response.data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch connection status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async () => {
    try {
      setProcessing(true);
      const response = await connectionService.followUser(userId);
      if (response.success) {
        toast.success('Successfully followed');
        await fetchConnectionStatus();
      }
    } catch (error) {
      console.error('Failed to follow:', error);
      toast.error(error.response?.data?.message || 'Failed to follow user');
    } finally {
      setProcessing(false);
    }
  };

  const handleUnfollow = async () => {
    try {
      setProcessing(true);
      const response = await connectionService.unfollowUser(userId);
      if (response.success) {
        toast.success('Unfollowed successfully');
        await fetchConnectionStatus();
      }
    } catch (error) {
      console.error('Failed to unfollow:', error);
      toast.error('Failed to unfollow user');
    } finally {
      setProcessing(false);
    }
  };

  const handleSendConnectionRequest = async () => {
    try {
      setProcessing(true);
      const response = await connectionService.sendConnectionRequest(userId, connectionMessage);
      if (response.success) {
        toast.success('Connection request sent');
        setShowMessageModal(false);
        setConnectionMessage('');
        await fetchConnectionStatus();
      }
    } catch (error) {
      console.error('Failed to send connection request:', error);
      toast.error(error.response?.data?.message || 'Failed to send connection request');
    } finally {
      setProcessing(false);
    }
  };

  const handleRemoveConnection = async () => {
    if (!confirm('Are you sure you want to remove this connection?')) return;
    
    try {
      setProcessing(true);
      const response = await connectionService.removeConnection(userId);
      if (response.success) {
        toast.success('Connection removed');
        await fetchConnectionStatus();
      }
    } catch (error) {
      console.error('Failed to remove connection:', error);
      toast.error('Failed to remove connection');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex gap-2">
        <div className="h-10 w-32 bg-gray-200 animate-pulse rounded-lg"></div>
        <div className="h-10 w-32 bg-gray-200 animate-pulse rounded-lg"></div>
      </div>
    );
  }

  if (status?.isOwnProfile) {
    return null;
  }

  return (
    <>
      <div className="flex flex-wrap gap-3">
        {/* Follow Button */}
        <Button
          onClick={status?.isFollowing ? handleUnfollow : handleFollow}
          disabled={processing}
          variant={status?.isFollowing ? 'outline' : 'primary'}
          className="!bg-navbar !border-navbar hover:!bg-primary-700 !text-white"
        >
          {processing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : status?.isFollowing ? (
            <UserCheck className="w-4 h-4" />
          ) : (
            <UserPlus className="w-4 h-4" />
          )}
          <span className="ml-2">{status?.isFollowing ? 'Following' : 'Follow'}</span>
        </Button>

        {/* Connect Button */}
        {status?.isConnected ? (
          <Button
            onClick={handleRemoveConnection}
            disabled={processing}
            variant="primary"
            className="!bg-green-600 !border-green-600 hover:!bg-green-700"
          >
            <UserCheck className="w-4 h-4" />
            <span className="ml-2">Connected</span>
          </Button>
        ) : status?.connectionStatus?.status === 'pending' ? (
          <Button
            disabled
            variant="outline"
            className="!bg-primary-50 !text-navbar !border-navbar cursor-not-allowed opacity-70"
          >
            <Loader2 className="w-4 h-4" />
            <span className="ml-2">{status.connectionStatus.isRequester ? 'Request Sent' : 'Request Received'}</span>
          </Button>
        ) : (
          <Button
            onClick={() => setShowMessageModal(true)}
            disabled={processing}
            variant="primary"
            className="!bg-[#c33c3d] !border-[#c33c3d] hover:!bg-[#a32f30] !text-white"
          >
            <Send className="w-4 h-4" />
            <span className="ml-2">Connect</span>
          </Button>
        )}
      </div>

      {/* Connection Message Modal */}
      {showMessageModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Send Connection Request</h3>
            <p className="text-sm text-gray-600 mb-4">
              Add a personal message to your connection request (optional)
            </p>
            <textarea
              value={connectionMessage}
              onChange={(e) => setConnectionMessage(e.target.value)}
              placeholder="Hi, I'd like to connect with you..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows={4}
              maxLength={200}
            />
            <p className="text-xs text-gray-400 mt-1 mb-4">
              {connectionMessage.length}/200 characters
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowMessageModal(false);
                  setConnectionMessage('');
                }}
                disabled={processing}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendConnectionRequest}
                disabled={processing}
                loading={processing}
                variant="primary"
                className="flex-1 !bg-[#c33c3d] !border-[#c33c3d] hover:!bg-[#a32f30] !text-white"
              >
                {!processing && <Send className="w-4 h-4 mr-2" />}
                {processing ? 'Sending...' : 'Send Request'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ConnectionActions;
